import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

import Quill from "quill";
// @ts-expect-error quilljs-markdown is not typed
import QuillMarkdown from "quilljs-markdown";
import { twJoin } from "tailwind-merge";

// biome-ignore lint/style/noRestrictedImports: This is a single place where we allow this import
import { mdService } from "src/base-components/TextEditor/MarkdownService";
import { useClampSelectionToEditor } from "src/base-components/TextEditor/useClampSelectionToEditor";
import { useEventListener } from "src/hooks/usehooks-ts";
import "src/base-components/TextEditor/styles.css";

type Range = { index: number; length: number };

type Props = {
  dataLoc?: string;
  toolbarRef?: React.MutableRefObject<Nullable<HTMLUListElement>>;
  placeholder?: string;
  readOnly?: boolean;
  autoFocus?: boolean;
  keepEmptyLines?: boolean;
  value: string;
  fullHeight?: boolean;
  inline?: boolean;
  maxHeight?: `max-h-[${number}px]` | "max-h-full";
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  onReady?: (quill: Quill) => void;
  /**
   * Tags passed to the QuillMarkdown plugin's `ignoreTags` option. Any tag in
   * this list is excluded from the plugin's on-the-fly markdown-shortcut
   * detection as the user types. For example, when `"header"` / `"h1"`…`"h6"`
   * are in the list, typing `# foo` at line start stays as literal text
   * instead of being converted to a heading.
   *
   * Defaults to `DEFAULT_IGNORE_MARKDOWN_TAGS` (headings, blockquote,
   * checkbox) — suitable for editors that hold short plain text such as
   * comments or descriptions, where accidental `#`-to-heading conversion is
   * undesirable. Pass a narrower list (e.g. `["blockquote", "checkbox"]`) to
   * enable heading shortcuts — useful for prose-style editors (prompts).
   *
   * Only read at Quill construction; runtime changes have no effect.
   */
  ignoreMarkdownTags?: readonly string[];
  /**
   * When `true`, clipboard events are intercepted on both sides:
   *
   * - **Paste**: if the `text/plain` payload contains block-level markdown
   *   tokens (headings, lists, blockquotes, fenced code), it is pre-converted
   *   via `mdService.toHtml` so Quill renders styled blocks rather than
   *   literal `##` / `- `. Rich `text/html` payloads (Docs, Notion, web
   *   pages) fall through to Quill's default clipboard.
   * - **Copy / cut**: the selected content is written to the clipboard as
   *   markdown on `text/plain` (so destinations like terminal / GitHub
   *   paste literal `#`/`**`/list syntax) and as semantic HTML on
   *   `text/html` (so rich-text destinations and paste back into this
   *   editor round-trip cleanly).
   *
   * Defaults to `false` — clipboard events fall through unchanged, preserving
   * existing behaviour for consumers that don't want surprise markdown
   * interpretation (e.g. pasting a shell script with `# comment` lines should
   * stay as plain text in a comment editor).
   */
  markdownAwareClipboard?: boolean;
  ref?: React.Ref<TextEditorRef>;
};

const defaultMaxHeight = "max-h-[300px]";

const DEFAULT_IGNORE_MARKDOWN_TAGS = [
  "header",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "checkbox",
];

const MARKDOWN_BLOCK_TOKEN_RE = /^(?:#{1,6}\s|[-*+]\s|\d+\.\s|>\s|```)/;

const looksLikeMarkdown = (text: string) =>
  text
    .split("\n")
    .some((line) => MARKDOWN_BLOCK_TOKEN_RE.test(line.trimStart()));

export type TextEditorRef = {
  quill: Quill | null;
  focus: () => void;
  getMarkdownValue: () => string;
};

// quilljs-markdown is not typed, we had to use any here
// biome-ignore lint/suspicious/noExplicitAny: quilljs-markdown is not typed
const Code: any = Quill.import("formats/code");
Code.className = "ql-code-highlighting";
Quill.register(Code, true);
Quill.register("modules/QuillMarkdown", QuillMarkdown, true);

export const TextEditor: React.FC<Props> = ({
  dataLoc,
  placeholder,
  readOnly = false,
  autoFocus = false,
  keepEmptyLines = false,
  inline = false,
  value: propsValue,
  onChange,
  onFocus,
  onBlur,
  onReady,
  fullHeight,
  toolbarRef,
  maxHeight,
  ignoreMarkdownTags = DEFAULT_IGNORE_MARKDOWN_TAGS,
  markdownAwareClipboard = false,
  ref,
}) => {
  const quill = useRef<Nullable<Quill>>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const getMarkdownValue = useCallback(() => {
    const html = quill.current?.getSemanticHTML() || "";
    return mdService.toMarkdown(html, { keepEmptyLines });
  }, [keepEmptyLines]);

  useImperativeHandle(ref, () => ({
    // A getter, not a snapshot: Quill is built later, in an effect.
    get quill() {
      return quill.current;
    },
    focus: () => {
      quill.current?.focus();
    },
    getMarkdownValue,
  }));

  const syncUserValueWithProps = useCallback(() => {
    const { current } = quill;
    if (!current) {
      return;
    }

    const html = mdService.toHtml(propsValue);

    // If the HTML is the same, we do not need to update the editor
    if (isEqualHtml(html, current.getSemanticHTML())) {
      return;
    }

    // Save current cursor position before updating content
    const currentSelection = current.getSelection();

    const delta = current.clipboard.convert({
      html,
    });
    current.setContents(delta);

    if (readOnly || !current.hasFocus()) {
      return;
    }

    // Restore cursor position, or set to end if previous position is invalid
    if (currentSelection) {
      const newLength = current.getLength();
      const restoredIndex = Math.min(
        currentSelection.index,
        newLength > 0 ? newLength - 1 : 0,
      );
      current.setSelection(restoredIndex, 0);
    } else {
      current.setSelection(current.getLength(), 0);
    }
  }, [propsValue, quill, readOnly]);

  const commitValue = useCallback(() => {
    if (onChange) {
      onChange(getMarkdownValue());
    }
  }, [onChange, getMarkdownValue]);

  useEffect(() => {
    // hack to sync value in next render cycle, othewise it doesn't work as expected
    Promise.resolve().then(() => {
      syncUserValueWithProps();
    });
  }, [propsValue, syncUserValueWithProps]);

  useEffect(() => {
    quill.current?.enable(!readOnly);
  }, [readOnly]);

  useEffect(() => {
    if (quill.current || !containerRef.current) {
      return;
    }

    const current = new Quill(containerRef.current, {
      placeholder,
      readOnly,
      modules: {
        toolbar: toolbarRef?.current,
        QuillMarkdown: { ignoreTags: ignoreMarkdownTags },
      },
    });

    quill.current = current;
    onReady?.(current);

    if (autoFocus && current) {
      setTimeout(() => {
        current.setSelection(current.getLength(), 0);
      });
    }
  }, [autoFocus, placeholder, toolbarRef, readOnly, onChange, onReady]);

  useEffect(() => {
    const handleSelectionChange = (range: Range, oldRange: Range) => {
      if (range === null && oldRange !== null) {
        onBlur?.();
      }

      if (range !== null && oldRange === null) {
        onFocus?.();
      }
    };

    quill.current?.on("selection-change", handleSelectionChange);

    return () => {
      quill.current?.off("selection-change", handleSelectionChange);
    };
  }, [quill, onFocus, onBlur]);

  useEffect(() => {
    const handleFocusOut = (e: FocusEvent) => {
      if (toolbarRef?.current?.contains(e.relatedTarget as Node)) {
        return;
      }
      commitValue();
    };

    const container = quill.current?.container;
    if (container) {
      container.addEventListener("focusout", handleFocusOut);
    }

    return () => {
      if (container) {
        container.removeEventListener("focusout", handleFocusOut);
      }
    };
  }, [quill, commitValue, toolbarRef]);

  useClampSelectionToEditor(quill);

  // Gated by `markdownAwareClipboard` — consumers without opt-in keep
  // Quill's default clipboard behaviour. Two symmetric handlers:
  //
  // - `paste`: when the payload is plain-text markdown, pre-convert via
  //   `mdService.toHtml` so Quill renders styled blocks instead of literal
  //   `##` / `- `. Rich `text/html` payloads fall through to Quill.
  // - `copy` / `cut`: write the selection as markdown on `text/plain` and
  //   semantic HTML on `text/html`, so plain-text destinations keep the
  //   markdown syntax while rich-text targets still paste formatted.
  useEffect(() => {
    if (!markdownAwareClipboard) return;
    const current = quill.current;
    if (!current) return;

    const handlePaste = (e: ClipboardEvent) => {
      if (readOnly) return;
      const cd = e.clipboardData;
      if (!cd || cd.getData("text/html")) return;
      const text = cd.getData("text/plain");
      if (!text || !looksLikeMarkdown(text)) return;

      e.preventDefault();
      e.stopPropagation();
      const range = current.getSelection(true);
      if (!range) return;
      if (range.length > 0) {
        current.deleteText(range.index, range.length, "user");
      }
      current.clipboard.dangerouslyPasteHTML(
        range.index,
        mdService.toHtml(text),
        "user",
      );
    };

    const writeSelectionToClipboard = (e: ClipboardEvent) => {
      const range = current.getSelection();
      if (!range || range.length === 0) return null;
      const html = current.getSemanticHTML(range.index, range.length);
      const markdown = mdService.toMarkdown(html, { keepEmptyLines });
      if (!markdown) return null;
      e.preventDefault();
      e.stopPropagation();
      e.clipboardData?.setData("text/plain", markdown);
      // Also write the semantic HTML so rich-text targets (Docs, Notion)
      // paste formatted content and — critically — so paste back into this
      // editor round-trips cleanly. Quill's default paste prefers
      // text/html, avoiding the ambiguity where markdown's `\n\n`
      // paragraph separator would otherwise be read as two `\n` line
      // breaks and produce an extra empty paragraph.
      e.clipboardData?.setData("text/html", html);
      return range;
    };

    const handleCopy = (e: ClipboardEvent) => {
      writeSelectionToClipboard(e);
    };

    const handleCut = (e: ClipboardEvent) => {
      if (readOnly) return;
      const range = writeSelectionToClipboard(e);
      if (range) current.deleteText(range.index, range.length, "user");
    };

    const container = current.container;
    container.addEventListener("paste", handlePaste, true);
    container.addEventListener("copy", handleCopy, true);
    container.addEventListener("cut", handleCut, true);
    return () => {
      container.removeEventListener("paste", handlePaste, true);
      container.removeEventListener("copy", handleCopy, true);
      container.removeEventListener("cut", handleCut, true);
    };
  }, [quill, readOnly, markdownAwareClipboard, keepEmptyLines]);

  // Commit value on outside click
  useEventListener("mousedown", (e) => {
    const [container, toolbar] = [containerRef.current, toolbarRef?.current];

    if (
      container?.contains(document.activeElement) &&
      !(
        container?.contains(e.target as Node) ||
        toolbar?.contains(e.target as Node)
      )
    ) {
      commitValue();
    }
  });

  return (
    <div
      ref={containerRef}
      className={twJoin(
        "decideScrollbar relative overflow-y-auto whitespace-pre-wrap font-inter-normal-13px text-gray-800",
        inline ? "inline-flex max-w-full" : "flex w-full",
        !readOnly && "min-h-[50px]",
        fullHeight && "h-full",
        maxHeight ? maxHeight : !readOnly && defaultMaxHeight,
      )}
      data-loc={dataLoc}
    />
  );
};

/**
 *  We do not want to compare large HTML strings, because it can be slow and
 *  it can cause performance issues. We just consider them notEqual in that case
 */

const isEqualHtml = (html1: string, html2: string) => {
  const SIZE_THRESHOLD = 10_000;

  if (html1.length > SIZE_THRESHOLD || html2.length > SIZE_THRESHOLD) {
    return false;
  }

  return html1.replace(/\n/g, "").trim() === html2.replace(/\n/g, "").trim();
};
